import React, { Dispatch, SetStateAction } from 'react'
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material'
import MultiSelectInput from '../../../Common/MultiSelectInput'
import DateTimeInput_EntityPreview from '../../../Common/DateTimeInput_EntityPreview'
import {
    ISelfAttributesRelationsData,
    SelectedRelationList,
} from '../../interfaces'
import {
    isChartableAndNotHidden,
    isChartableOrMappableOrTimeSeriesAndNotHidden,
} from '../../../../utils/AttributeUtils'
import { useTranslation } from 'react-i18next'
import TruncatedText from '../../../Common/TruncatedText'
import { useAppSelector } from '../../../../redux/hook'

interface AttributeFilterPanelProps {
    selectedValue: string[]
    onSelectValue: (id: string, value: any) => void
    dataList: ISelfAttributesRelationsData[]
    relationsDropdownToDisplay: ISelfAttributesRelationsData[]
    selectedRelationList: SelectedRelationList[]
    onSelectRelationsAttribute: (id: string, value: string[]) => void
    selectedPeriod: string
    onChangePeriod: (event: SelectChangeEvent) => void
    periodOptions: string[]
    startDate: string
    endDate: string
    setStartDate: (date: string) => void
    setEndDate: (date: string) => void
    entityInstanceCreatedDate: string
    entityInstanceEndDate?: string
    showWithinGeofence: boolean
    setShowWithinGeofence: Dispatch<SetStateAction<boolean>>
    isIntransitShipmentPreview: boolean
}

const customDropdownWidth = 120

const ViewEntityAttributeFilterPanel: React.FC<AttributeFilterPanelProps> = ({
    selectedValue,
    onSelectValue,
    dataList,
    relationsDropdownToDisplay,
    selectedRelationList,
    onSelectRelationsAttribute,
    selectedPeriod,
    onChangePeriod: onChagePeriod,
    periodOptions,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    entityInstanceCreatedDate,
    entityInstanceEndDate,
    // showWithinGeofence,
    // setShowWithinGeofence,
    isIntransitShipmentPreview,
}) => {
    const { t } = useTranslation()
    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )
    const displayAM_PM = userLocalization?.timeFormat.startsWith('hh:mm')
    let userPreferredDateFormat = 'MM/DD/YYYY HH:mm'
    if (userLocalization?.dateFormat && userLocalization?.timeFormat) {
        const timeFormat = userLocalization?.timeFormat
            ?.split(':')
            .slice(0, 2)
            .join(':')
        const A = userLocalization?.timeFormat?.includes('A') ? 'A' : ''
        userPreferredDateFormat = `${userLocalization.dateFormat} ${timeFormat} ${A}`
    }

    return (
        <Box display="flex" gap={1} sx={{ padding: '10px 10px' }}>
            {/* Left Side: Attribute Selectors */}
            <Box display="flex" gap={1} sx={{ flexWrap: 'wrap', flex: 1 }}>
                {dataList.length > 0 && (
                    <Box sx={{ width: 160 }}>
                        <MultiSelectInput
                            id="entity-attribute-or-relation"
                            label=""
                            value={selectedValue}
                            onChange={onSelectValue}
                            list={dataList.map((i) => {
                                const name = i.isRelation
                                    ? i.relatedEntityInstance?.uid
                                    : i.selfAttribute?.name
                                return {
                                    id: i.id,
                                    name,
                                }
                            })}
                            ui_width="160px"
                            allowClear={false}
                        />
                    </Box>
                )}

                {relationsDropdownToDisplay.map((item) => {
                    const relatedEntityInstance = item.relatedEntityInstance!
                    const chartableMappableTimeSeriesAttributes =
                        relatedEntityInstance?.attributes.filter(
                            isIntransitShipmentPreview
                                ? isChartableAndNotHidden
                                : isChartableOrMappableOrTimeSeriesAndNotHidden,
                        )
                    const selectedItems =
                        selectedRelationList
                            .find(
                                (relation) =>
                                    relation.relatedEntityInstanceId ===
                                    relatedEntityInstance.entityInstanceId,
                            )
                            ?.selectedAttributes.map(
                                (attribute) => attribute.id,
                            ) || []

                    return (
                        <Box sx={{ width: 160 }} key={item.id}>
                            <MultiSelectInput
                                id={item.id}
                                label={relatedEntityInstance?.uid}
                                isLegend={true}
                                value={selectedItems}
                                onChange={onSelectRelationsAttribute}
                                list={chartableMappableTimeSeriesAttributes?.map(
                                    (attr) => ({
                                        id: attr.id,
                                        name: attr.name,
                                    }),
                                )}
                                ui_width="160px"
                                allowClear={false}
                            />
                        </Box>
                    )
                })}
            </Box>

            {/* Right Side: Filters and Toggles */}
            <Box
                display="flex"
                flexDirection="column"
                gap={1}
                sx={{
                    marginLeft: 'auto',
                    alignItems: 'flex-end',
                }}
            >
                {/* Top Row: Period, Start/End */}
                <Box
                    display="flex"
                    gap={1}
                    flexWrap="wrap"
                    justifyContent="flex-end"
                >
                    <Box sx={{ width: customDropdownWidth }}>
                        <FormControl fullWidth>
                            <InputLabel id="graph-type-select-label">
                                {t('period')}
                            </InputLabel>
                            <Select
                                labelId="graph-type-select-label"
                                id="graph-type-select"
                                value={selectedPeriod}
                                label={t('period')}
                                onChange={onChagePeriod}
                                size="small"
                                sx={{ fontSize: '14px' }}
                                MenuProps={{
                                    anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    },
                                    transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left',
                                    },
                                    PaperProps: {
                                        style: {
                                            maxHeight: 280,
                                            maxWidth:
                                                2 * customDropdownWidth - 40,
                                        },
                                    },
                                }}
                            >
                                {periodOptions?.map((p) => (
                                    <MenuItem key={p} value={p} id={p}>
                                        <TruncatedText text={p} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* {selectedPeriod === 'Custom' && (
                        <> */}
                    <Box sx={{ width: 190 }}>
                        <DateTimeInput_EntityPreview
                            id="graph-start-date"
                            label={t('startDate')}
                            value={startDate}
                            isLegend={true}
                            onChange={(id: any, d: any) =>
                                setStartDate(d ? d.utc().format() : '')
                            }
                            minDateTime={entityInstanceCreatedDate}
                            maxDateTime={
                                entityInstanceEndDate ||
                                new Date().toISOString()
                            }
                            dateFormat={userPreferredDateFormat}
                            displayAM_PM={displayAM_PM}
                            timezone={userLocalization?.timeZone}
                        />
                    </Box>
                    <Box sx={{ width: 190 }}>
                        <DateTimeInput_EntityPreview
                            id="graph-end-date"
                            label={t('endDate')}
                            value={endDate}
                            isLegend={true}
                            onChange={(id: any, d: any) =>
                                setEndDate(d ? d.utc().format() : '')
                            }
                            minDateTime={startDate}
                            maxDateTime={
                                entityInstanceEndDate ||
                                new Date().toISOString()
                            }
                            dateFormat={userPreferredDateFormat}
                            displayAM_PM={displayAM_PM}
                            timezone={userLocalization?.timeZone}
                        />
                    </Box>
                    {/* </>
                    )} */}
                </Box>

                {/* Bottom Row: Timezone + Geofence Radio */}
                <Box display="flex" gap={1} alignItems="center">
                    {/* <FormControl>
                        <RadioGroup
                            row
                            aria-labelledby="graph-geofence-radio-buttons"
                            name="graph-geofence-row-radio-buttons-group"
                            value={showWithinGeofence}
                            onChange={() =>
                                setShowWithinGeofence(!showWithinGeofence)
                            }
                        >
                            <FormControlLabel
                                value={true}
                                control={<Radio />}
                                label={t('withinGeofence')}
                            />
                            <FormControlLabel
                                value={false}
                                control={<Radio />}
                                label={t('outsideGeofence')}
                            />
                        </RadioGroup>
                    </FormControl> */}
                </Box>
            </Box>
        </Box>
    )
}

export default ViewEntityAttributeFilterPanel
